/**
 * A chain of taps → the structure walls it stands for.
 *
 * The chain-drawing tool (`hooks/useWallChainDrawing`) hands over the points
 * the user tapped; each consecutive pair becomes one structure wall centred on
 * the line between them. Consecutive walls' centrelines therefore meet EXACTLY
 * at the tap — the room-split engine joins a chain's walls end to end within
 * 1 cm (`shared-lib/room-split/chain.js`, `buildChain`), so a wall may never
 * be lengthened past its tap.
 *
 * Every wall of a chain gets the storey, the floor and the wall height of the
 * room the chain is drawn in. Today's single placement read the floor from the
 * whole structure and set no storey at all, so on a stacked structure a wall
 * drawn upstairs stood on the ground floor and the split looked at both.
 *
 * Its TOP meets the room's ceiling (`wallUnderCeiling`): under a slope it
 * slopes, under a soffit it steps down to the soffit's underside. At the room's
 * typical wall height alone, a wall stopped short of a raised ceiling and
 * poked through a lowered one. The top is the wall's elevation outline
 * (`polygonCorners`); each of its room faces reads the ceiling along its own
 * line, and so does each face a room split lays on the new rooms' edges.
 *
 * Bends. Two walls centred on their taps and cut square leave a notch of half
 * a thickness on the OUTSIDE of every bend. The plan footprint and the 3D
 * solid both shear a wall's BACK face by `thickness × tan(leftMiter |
 * rightMiter)` (`calculateWallFootprint`, `mitredWallCaps`), with
 * `frontFacesSameDirection` saying which face is the back. So at a bend the
 * back is put on the outside and carried on to where the two outside faces
 * meet: `half × tan(θ/2)` past the tap, θ the turn. The inside face is left
 * running to the tap, inside the other wall's body — the outline is exact and
 * the centreline untouched. One `frontFacesSameDirection` per wall means a
 * wall whose two bends turn opposite ways (the middle of an S) can mitre only
 * one of them; it takes the sharper.
 *
 * Pure. X-Z plane; Y is up; increasing Z is screen-down (CLAUDE.md).
 */

import { isPointInsidePolygon } from '@shared-lib/geometry/pointInPolygon';
import { MAX_MITER_ANGLE_RAD } from '@shared-lib/geometry/corner-adjustment';
import {
  profileUnderCeiling,
  profileOfOutline,
  outlineCorners,
  profileTop,
} from '@shared-lib/geometry/wallTopProfile';
import {
  JOIN_TOL_M,
  TOUCH_TOL_M,
  distanceToPolygonBoundary,
  roomFloorPolygonXZ,
} from '@shared-lib/room-split';
import {
  DEFAULT_WALL_HEIGHT,
  createStructureWall,
  createRoomWallsForStructureWall,
  createRoomWallsInsideRooms,
  getRoomWallHeight,
} from './wallCreationUtils';
import { findFloorY } from './floorPlanUtils';
import { formatFeetInches } from './dimensionFormat';
import { wallFootprintCorners } from './roomSnapInference';

const dist = (a, b) => Math.hypot(b.x - a.x, b.z - a.z);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 });

function roomList(roomsData) {
  return roomsData?.values ? [...roomsData.values()] : [];
}

/**
 * The centre, length and rotation of the wall a stretch from `a` to `b`
 * becomes — centred on its taps, its local +X along travel
 * (`rotationY = atan2(-dz, dx)`, the `rotatePointY` convention).
 */
export function stretchGeometry(a, b) {
  return {
    center: mid(a, b),
    length: dist(a, b),
    rotationY: Math.atan2(-(b.z - a.z), b.x - a.x),
  };
}

/** Rooms on `story`; an unstamped storey (room or active) matches anything. */
function roomsOnStorey(rooms, story) {
  if (!Number.isFinite(story)) return rooms;
  return rooms.filter((r) => !Number.isFinite(r?.story) || r.story === story);
}

/**
 * The room a chain is drawn in: the one on the active storey containing the
 * middle of the first stretch. Not the first tap itself — that is usually
 * snapped onto a wall face, which is the room's boundary, where a
 * point-in-polygon test answers either way.
 */
export function chainRoom(taps, roomsData, story) {
  if (!Array.isArray(taps) || taps.length < 2) return null;
  const probe = mid(taps[0], taps[1]);
  for (const room of roomsOnStorey(roomList(roomsData), story)) {
    const poly = roomFloorPolygonXZ(room);
    if (poly.length >= 3 && isPointInsidePolygon(probe, poly)) return room;
  }
  return null;
}

/** The turn at tap `j` between the stretches either side of it. */
function bendAt(taps, j) {
  const a = taps[j - 1];
  const b = taps[j];
  const c = taps[j + 1];
  const la = dist(a, b);
  const lc = dist(b, c);
  const d0 = { x: (b.x - a.x) / la, z: (b.z - a.z) / la };
  const d1 = { x: (c.x - b.x) / lc, z: (c.z - b.z) / lc };
  const cross = d0.x * d1.z - d0.z * d1.x; // > 0: turns left of travel
  const theta = Math.atan2(Math.abs(cross), d0.x * d1.x + d0.z * d1.z);
  return { theta, turnsLeft: cross > 0 };
}

/**
 * The bend mitres of each stretch: `{ frontFacesSameDirection, leftMiter,
 * rightMiter }` per stretch, or null for a stretch cut square at both ends.
 *
 * A wall's local +Z is left of its travel (`rotationY = atan2(-dz, dx)`), and
 * `frontFacesSameDirection: true` puts the FRONT there and the sheared back on
 * the right. A left turn has its outside on the right, so it wants `true`.
 * The miter is the back face's extension over the full thickness, so the
 * outside face's `half × tan(θ/2)` is `tan(miter) = tan(θ/2) / 2`.
 */
export function chainBendMiters(taps) {
  const n = taps.length - 1;
  const ends = Array.from({ length: n }, () => ({ left: null, right: null }));
  for (let j = 1; j < n; j++) {
    const bend = bendAt(taps, j);
    if (bend.theta < 1e-9 || bend.theta / 2 > MAX_MITER_ANGLE_RAD) continue;
    ends[j - 1].right = bend;
    ends[j].left = bend;
  }
  return ends.map(({ left, right }) => {
    if (!left && !right) return null;
    // One front per wall: where the two bends disagree, the sharper wins and
    // the other end stays square.
    const lead =
      left && right
        ? left.theta >= right.theta
          ? left
          : right
        : left || right;
    const fsd = lead.turnsLeft;
    const miter = (bend) =>
      bend && bend.turnsLeft === fsd
        ? Math.atan(Math.tan(bend.theta / 2) / 2)
        : 0;
    return {
      frontFacesSameDirection: fsd,
      leftMiter: miter(left),
      rightMiter: miter(right),
    };
  });
}

/**
 * The structure walls a chain of taps stands for.
 *
 * @param {{x:number,z:number}[]} taps in drawing order
 * @param {Map|Array} roomsData the room store (anything with `.values()`)
 * @param {{ story?: number|null }} [opts] the floor picker's storey; null or
 *   absent = all floors
 * @returns {{ walls: object[], roomId: string|null }} one wall per stretch
 *   (stretches shorter than the chain's 1 cm joint tolerance are dropped), and
 *   the room the chain is drawn in
 */
export function chainWallsFromTaps(taps, roomsData, { story = null } = {}) {
  const pts = Array.isArray(taps) ? taps : [];
  const room = chainRoom(pts, roomsData, story);
  const storeyRooms = roomsOnStorey(roomList(roomsData), story);

  const floorY = room
    ? findFloorY({ floors: room.floors })
    : findFloorY({ rooms: storeyRooms });
  const height = room
    ? getRoomWallHeight(room, DEFAULT_WALL_HEIGHT)
    : DEFAULT_WALL_HEIGHT;
  const wallStory = Number.isFinite(room?.story) ? room.story : story;

  // A repeated tap is a zero-length stretch: drop it before anything reads
  // the stretches, so a bend is always between two real ones.
  const kept = pts.filter(
    (p, i) => i === 0 || dist(pts[i - 1], p) > JOIN_TOL_M
  );
  const miters = chainBendMiters(kept);

  const walls = [];
  for (let i = 0; i + 1 < kept.length; i++) {
    const { center, length, rotationY } = stretchGeometry(kept[i], kept[i + 1]);
    const wall = wallUnderCeiling(
      createStructureWall({
        center: { x: center.x, y: 0, z: center.z },
        length,
        height,
        rotationY,
        story: wallStory,
        floorY,
      }),
      room,
      { a: kept[i], b: kept[i + 1], floorY, height }
    );
    walls.push(miters[i] ? { ...wall, ...miters[i] } : wall);
  }
  return { walls, roomId: room?.id ?? null };
}

/**
 * The wall with the top the ceiling over it needs: its outline runs from `a`
 * to `b` along its centreline on `floorY`, the lowest ceiling facet over each
 * point its top (`profileUnderCeiling`), `height` where none covers it. A room
 * with no ceiling leaves the wall as it is.
 */
export function wallUnderCeiling(wall, room, { a, b, floorY, height }) {
  if (!room?.ceilings?.length) return wall;
  const profile = profileUnderCeiling(room.ceilings, a, b, { floorY, height });
  const top = profileTop(profile);
  return {
    ...wall,
    center: { ...wall.center, y: floorY + top / 2 },
    dimensions: { ...wall.dimensions, y: top },
    polygonCorners: outlineCorners(profile, a, b, floorY),
  };
}

/**
 * A drawn wall after Extend to wall (`planExtendToWall`'s `update`), with the
 * top the ceiling over all of its new length needs — the outline it was drawn
 * with covers only its old length.
 *
 * @param {object} wall the structure wall as it was
 * @param {{ center, dimensions, rotation }} update
 * @param {Map|Array} roomsData the room store
 * @param {{ story?: number|null }} [opts]
 * @returns {object} the wall as it is now
 */
export function extendedWallUnderCeiling(
  wall,
  update,
  roomsData,
  { story = null } = {}
) {
  const next = { ...wall, ...update };
  const ry = next.rotation?.y || 0;
  const half = (next.dimensions?.x || 0) / 2;
  const d = { x: Math.cos(ry), z: -Math.sin(ry) };
  const a = { x: next.center.x - d.x * half, z: next.center.z - d.z * half };
  const b = { x: next.center.x + d.x * half, z: next.center.z + d.z * half };
  const room = chainRoom([a, b], roomsData, story);
  if (!room) return next;
  return wallUnderCeiling(next, room, {
    a,
    b,
    floorY: findFloorY({ floors: room.floors }),
    height: getRoomWallHeight(room, DEFAULT_WALL_HEIGHT),
  });
}

/**
 * A face of `wall` given the top it needs. A wall drawn to meet the ceiling
 * (it carries an outline) gets faces that meet it too, each read along its
 * OWN line: where the ceiling steps between the wall's two faces, one face is
 * under the higher part and one under the lower. Outside a room with a
 * ceiling, the face takes the wall's outline; a wall with none leaves the face
 * as it is.
 */
function withWallTop(face, wall, room) {
  const own = profileOfOutline(wall);
  const bl = face?.alignment?.bottomLeft;
  const br = face?.alignment?.bottomRight;
  if (!own || !bl || !br) return face;
  const profile = room?.ceilings?.length
    ? profileUnderCeiling(room.ceilings, bl, br, {
        floorY: bl.y,
        height: getRoomWallHeight(room, DEFAULT_WALL_HEIGHT),
      })
    : own;
  const polygonCorners = outlineCorners(profile, bl, br, bl.y);
  const height = Math.max(...polygonCorners.map((p) => p.y)) - bl.y;
  return {
    ...face,
    center: { ...face.center, y: bl.y + height / 2 },
    dimensions: { ...face.dimensions, y: height },
    polygonCorners,
  };
}

/** A wall's footprint as a ring (`wallFootprintCorners` is not in ring order). */
export function footprintRing(wall) {
  const c = wallFootprintCorners(wall);
  return c.length === 4 ? [c[0], c[2], c[3], c[1]] : [];
}

/**
 * Whether `point` lies on a wall: within `TOUCH_TOL_M` (the chain-end touch
 * tolerance the split engine uses) of the edge of any wall's footprint — a
 * face or an end. A point deep inside a wall's body is not on its edge.
 */
export function pointTouchesWall(point, walls) {
  return (walls || []).some((wall) => {
    const ring = footprintRing(wall);
    return (
      ring.length === 4 && distanceToPolygonBoundary(point, ring) <= TOUCH_TOL_M
    );
  });
}

/** A wall's two centreline ends: its local −X ("start") and +X ("end"). */
function wallEnds(wall) {
  const ry = wall.rotation?.y ?? 0;
  const d = { x: Math.cos(ry), z: -Math.sin(ry) };
  const h = (wall.dimensions?.x ?? 0) / 2;
  return [
    { x: wall.center.x - d.x * h, z: wall.center.z - d.z * h },
    { x: wall.center.x + d.x * h, z: wall.center.z + d.z * h },
  ];
}

/**
 * Which end caps of `wall` to leave off (`createRoomWallsForStructureWall`'s
 * `snappedEndpoint`): an end that meets another wall — a wall it runs into, or
 * its neighbour in a chain, whose footprint the joint lies on — has its cap
 * sitting on that wall, so only a FREE end keeps one.
 *
 * @returns {'start'|'end'|'both'|null}
 */
export function wallCapsToSkip(wall, otherWalls) {
  const [a, b] = wallEnds(wall);
  const start = pointTouchesWall(a, otherWalls);
  const end = pointTouchesWall(b, otherWalls);
  if (start && end) return 'both';
  if (start) return 'start';
  if (end) return 'end';
  return null;
}

/** The faces a wall gives the rooms it stands in, by room id. */
function facesForWall(wall, rooms, otherWalls) {
  const at = { x: wall.center.x, z: wall.center.z };
  const room = rooms.find((r) => {
    const poly = roomFloorPolygonXZ(r);
    return poly.length >= 3 && isPointInsidePolygon(at, poly);
  });
  const topped = (faces, r) => faces.map((f) => withWallTop(f, wall, r));
  if (room) {
    const snappedEndpoint = wallCapsToSkip(wall, otherWalls);
    return new Map([
      [
        room.id,
        topped(
          createRoomWallsForStructureWall(wall, { snappedEndpoint }),
          room
        ),
      ],
    ]);
  }
  // In no room: clipped into whichever rooms its faces cross, as a single
  // drawn wall's are.
  const byRoom = createRoomWallsInsideRooms(wall, rooms);
  return new Map(
    [...byRoom].map(([id, faces]) => [
      id,
      topped(
        faces,
        rooms.find((r) => r.id === id)
      ),
    ])
  );
}

/**
 * The room faces of a chain's walls, by room: `Map<roomId, roomWall[]>`.
 *
 * A wall whose centre is in a room (on the active storey) gives that room its
 * two long faces and any end cap still showing; a wall in no room has its
 * faces clipped into whichever rooms they cross, as a single drawn wall's are.
 *
 * @param {object[]} walls `chainWallsFromTaps(…).walls`
 * @param {Map|Array} roomsData the room store
 * @param {{ story?: number|null, structureWalls?: object[] }} [opts] the
 *   storey, and the structure's walls the chain's ends may meet
 */
export function chainRoomFaces(
  walls,
  roomsData,
  { story = null, structureWalls = [] } = {}
) {
  const rooms = roomsOnStorey(roomList(roomsData), story);
  const byRoom = new Map();
  for (const wall of walls) {
    // Its neighbours in the chain count as walls its ends meet.
    const others = [...structureWalls, ...walls.filter((w) => w !== wall)];
    for (const [roomId, faces] of facesForWall(wall, rooms, others)) {
      if (!faces.length) continue;
      byRoom.set(roomId, [...(byRoom.get(roomId) || []), ...faces]);
    }
  }
  return byRoom;
}

/**
 * A wall's room faces rebuilt after its geometry changed (Extend to wall):
 * every face that points at it is dropped from every room, and fresh ones are
 * made from the wall as it is now — capped only at an end still free.
 *
 * @param {Map|Array} roomsData the room store
 * @param {object} wall the structure wall, updated
 * @param {object[]} otherWalls every other structure wall it may meet
 * @param {{ story?: number|null }} [opts]
 * @returns {Map<string, object>} the rooms that changed, by id — new objects;
 *   the rooms handed in are untouched
 */
export function rebuildWallFaces(
  roomsData,
  wall,
  otherWalls,
  { story = null } = {}
) {
  const all = roomList(roomsData);
  const isMine = (w) => w?.alignment?.structureWallId === wall.id;
  const changed = new Map();
  for (const room of all) {
    if (!(room.walls || []).some(isMine)) continue;
    changed.set(room.id, {
      ...room,
      walls: room.walls.filter((w) => !isMine(w)),
    });
  }
  const current = all.map((r) => changed.get(r.id) || r);
  const rooms = roomsOnStorey(current, story);
  for (const [roomId, faces] of facesForWall(wall, rooms, otherWalls)) {
    if (!faces.length) continue;
    const room = changed.get(roomId) || current.find((r) => r.id === roomId);
    changed.set(roomId, { ...room, walls: [...(room.walls || []), ...faces] });
  }
  return changed;
}

/** The text of the drawing readout: `10'-6"`, or `4'-0" · 45°` off square. */
export function chainReadoutText(readout) {
  if (!readout) return null;
  const length = formatFeetInches(readout.lengthM);
  return readout.angleDeg == null
    ? length
    : `${length} · ${Math.round(readout.angleDeg)}°`;
}
